import React, { useState, useEffect, useRef, useContext } from 'react';

import { useQuery, useMutation } from '@apollo/client';
import { ADD_LAST_READ_PAGE } from '@graphql/lastreadpage';
import { GET_ELIB_LIMIT, GET_ELIB_LIMIT_UPDATE_OR_CREATE } from '@graphql/editor';

import { Context } from '@store/store';

import {
  StyledPageContainer,
  StyledPagesContainer,
} from './page-container.styles';

import PageFrame from './page-frame';
import PageContextMenu from './page-context-menu';

const PageContainer = ({ pages, bookId, userId, bookTitle, bookAuthors, initials, pageCount, audience, publisher, pubDate }) => {
  const [state, dispatch] = useContext(Context);
  const [isPagesLoading, setIsPagesLoading] = useState(true);
  const [pagesHTML, setPagesHTML] = useState(null);

  const [setLastReadPage] = useMutation(ADD_LAST_READ_PAGE);

  const [counts, setCounts] = useState(0);
  const refValue = useRef(counts);  
  const { loading:limitLoading, error:limitError, data:limitData, refetch } = useQuery(GET_ELIB_LIMIT, {
    variables: { book_id: bookId, user_id: userId },
    fetchPolicy: "no-cache",
  });
  const [ updateOrCreateLimit, { data:limitUpdateData, loading:limitUpdateLoading, error:limitUpdateError }] = useMutation(GET_ELIB_LIMIT_UPDATE_OR_CREATE);

  useEffect(() => {
    refValue.current = counts;
  })
  
  const container = document.querySelector('#pageList');

  useEffect(() => {
  state.isUserLoggedIn && !['12868'].includes(audience?.uid) && container &&
    container.addEventListener('copy', (event) => {
      const selection = document.getSelection();
      const text = selection.toString();

      if (refValue.current <= 10000) {
        const textHtml = `<br><p data-id=${bookId}>${text}</p><br>`;
        const blobHtml = new Blob([textHtml], { type: "text/html" });
        const blobText = new Blob([text], { type: "text/plain" });
        const dataHtml = [new window.ClipboardItem({
            ["text/plain"]: blobText,
            ["text/html"]: blobHtml,
        })];

        event.preventDefault();
        const initialsSet = bookAuthors ? bookTitle+" / "+bookAuthors : bookTitle;
        const publisherSet = publisher ? publisher+", " : "";
        const pubSet = pubDate ? pubDate+". " : " ";
        const pageSet = pageCount ? pageCount+" с. " : " ";

        const today = new Date();
        const yyyy = today.getFullYear();
        let mm = today.getMonth() + 1;
        let dd = today.getDate();
        if (dd < 10) dd = '0' + dd;
        if (mm < 10) mm = '0' + mm;
        const formattedToday = dd + '.' + mm + '.' + yyyy;
        //event.clipboardData.setData('text/plain', 'foo');
        navigator.clipboard.write(dataHtml);
        event.clipboardData.setData('text/html', `<br><p data-id=${bookId}>${text}</p><br>`);
        event.clipboardData.setData('text/plain', `${text}`);
        event.preventDefault();
        updateOrCreateLimit({
          variables: {
            book_id: bookId,
            user_id: userId,
            content: `${text}`,
          },
          fetchPolicy: "no-cache",
        }).then((res) => {
          setCounts(res?.data?.elibReaderLimitUpdateOrCreate?.count);
          dispatch({ type: 'SHOW_EDITOR_MODAL', payload: true, action: 'Скопировано' });
        });
      } else {
        navigator.clipboard.writeText('');
        event.clipboardData.setData('text/html', ``);
        event.clipboardData.setData('text/plain', ``);
        event.preventDefault();
        dispatch({ type: 'SHOW_EDITOR_MODAL', payload: true, action: 'Вы достигли ограничений в 10000 символов' });
      }
    });
  }, [container, refValue.current]);

  const preloadNextPages = (data) => {
    try {
      Promise.all(
        data.map((page) => {
          return fetch(page);
        }),
      ).then(responses => {
        Promise.all(responses.map(res => res.text()))
          .then(htmlPage => {
            dispatch({ type: 'SET_PRELOADPAGE', payload: htmlPage });
          })
      })
    } catch {
      return
    }
  }

  const fetchPages = (data) => {
    if (state.currentPage !== state.preloadPageNum) {
      Promise.all(
        data.map((page) => {
          return fetch(page);
        }),
      ).then(responses => {
        Promise.all(responses.map(res => res.text()))
          .then(htmlPage => {
            setPagesHTML(htmlPage);
          })
          .finally(() => {
            setIsPagesLoading(false);
          });
      })
    } else {
      setPagesHTML(state.preloadPage)
      setIsPagesLoading(false);
    }
  };

  const fetchSinglePages = async () => {
    setIsPagesLoading(true);
    fetchPages([pages[state.currentPage]]);
    preloadNextPages([pages[state.currentPage + 1]]);
  };

  const fetchDualPages = async () => {
    setIsPagesLoading(true);
    const currentDualPage = Math.ceil(state.currentPage / 2);
    fetchPages(state.pairsPagesHtmlUrls[currentDualPage]);
    preloadNextPages(state.pairsPagesHtmlUrls[currentDualPage + 1]);
  };

  // кастомное контекстное меню, появляющееся при клике правой кнопкой мыши на текст ЭД
  const [getContextMenuState, setContextMenuState] = useState('CLOSED');
  const [getContextMenuAnchorPoint, setContextMenuAnchorPoint] = useState({ x: 0, y: 0 });
  const getSelectionText = function () {
    let text = '';
    if (window.getSelection) {
      text = window.getSelection().toString();
    } else if (document.selection && document.selection.type != "Control") {
      text = document.selection.createRange().text;
    }
    return text;
  }
  const handlePageContextMenu = (e) => {
    e.preventDefault();

    let selectedText = getSelectionText();
    if (selectedText == null || selectedText.trim() == '') {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      range.expand('word');

      let selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range)

      selectedText = getSelectionText();
      if (selectedText == null || selectedText.trim() == '') {
        selection.removeAllRanges();
        setContextMenuState('CLOSED');
        return;
      }
    }

    setContextMenuAnchorPoint({ x: e.clientX, y: e.clientY + 10 });
    setContextMenuState('OPENED_BY_RIGHTCLICK');
  }
  
  useEffect(() => {
    if (limitData?.elibReaderLimit?.count) {
      setCounts(limitData?.elibReaderLimit?.count);
    }
    if (state.isUserLoggedIn) {
      setLastReadPage({ variables: { bookId: state.bookId, page: state.currentPage } })
        .catch((err) => {
          const errors = err.graphQLErrors.map(error => error);
          console.log(errors)
          dispatch({
            type: 'SET_IS_USER_LOGGED_IN',
            payload: false,
          });
          dispatch({
            type: 'TOGGLE_LOGIN_MODAL_VISIBILITY',
            payload: true,
          });
          dispatch({ type: 'WOBBLE_LOGIN_MODAL', payload: true });
        });
    }

    if (state.isSinglePageMode) {
      fetchSinglePages();
    } else {
      fetchDualPages();
    }
  }, [state.currentPage, state.isSinglePageMode, limitData]);


  return (
    <StyledPageContainer id="page-container">
      {isPagesLoading ? (
        <p style={{ margin: 'auto' }}>Загрузка...</p>
      ) : (
        <>
          {["48059", "42050", "175723", "40865", "66221", "13679", "89060", "65075", '12868'].includes(audience?.uid) || ["2","4","5","6","9","10","11","12","13","14"].includes(audience?.target_audience_id) ?
            <>
            <StyledPagesContainer id="pageList" onContextMenu={(e) => handlePageContextMenu(e)} style={{ transform: `rotate(${state.rotatePage}deg)`, marginBottom: state.rotatePage === 90 || state.rotatePage === 270 ? '1rem' : '' }}>
              {Array.isArray(pagesHTML) &&
                pagesHTML.map((page, index) => (
                  <PageFrame key={index} pageFrameTags={page} bookId={bookId} />
                ))}
            </StyledPagesContainer>
            <PageContextMenu
              getContextMenuState={getContextMenuState}
              setContextMenuState={setContextMenuState}
              getContextMenuAnchorPoint={getContextMenuAnchorPoint}
              setContextMenuAnchorPoint={setContextMenuAnchorPoint}
            />
            </>
          :
            <StyledPagesContainer id="pageList" style={{ transform: `rotate(${state.rotatePage}deg)`, marginBottom: state.rotatePage === 90 || state.rotatePage === 270 ? '1rem' : '' }}>
              {Array.isArray(pagesHTML) &&
                pagesHTML.map((page, index) => (
                  <PageFrame key={index} pageFrameTags={page} bookId={bookId} />
                ))}
            </StyledPagesContainer>
        }
        </>
      )}
    </StyledPageContainer>
  );
};

export default PageContainer;